import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, RefreshCw, ArrowLeft, Download, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchLeads, fetchLeadStats, updateLeadStatus, deleteLead, fetchMe, logout, BRAND } from "@/lib/api";
import { StatsCards } from "@/components/admin/StatsCards";
import { LeadsTable } from "@/components/admin/LeadsTable";
import { AdminLogin } from "@/components/admin/AdminLogin";

export default function AdminLeads() {
  const [user, setUser] = useState(null); // null = verificando, false = sin sesión
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const handleUnauthorized = (err) => {
    if (err?.response?.status === 401) {
      setUser(false);
      return true;
    }
    return false;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(), fetchLeadStats()]);
      setLeads(l);
      setStats(s);
    } catch (err) {
      if (!handleUnauthorized(err)) toast.error("No se pudieron cargar los leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe().then(setUser).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      setUser(false);
      setLeads([]);
      setStats(null);
      toast.success("Sesión cerrada");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    const qDigits = q.replace(/\D/g, "");
    return leads.filter(
      (l) => l.nombre.toLowerCase().includes(q) || (qDigits && l.telefono.replace(/\D/g, "").includes(qDigits))
    );
  }, [leads, query]);

  const handleStatus = async (id, status) => {
    try {
      const updated = await updateLeadStatus(id, status);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      setStats(await fetchLeadStats());
      toast.success("Estado actualizado");
    } catch (err) {
      if (!handleUnauthorized(err)) toast.error("No se pudo actualizar el estado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este lead? Esta acción no se puede deshacer.")) return;
    try {
      await deleteLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setStats(await fetchLeadStats());
      toast.success("Lead eliminado");
    } catch (err) {
      if (!handleUnauthorized(err)) toast.error("No se pudo eliminar el lead");
    }
  };

  const exportCsv = () => {
    const rows = [["Nombre", "Teléfono", "Fecha", "Estado", "Bono"], ...filtered.map((l) => [l.nombre, l.telefono, l.created_at, l.status, l.reward_code])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-mi-proteccion-365-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--slate-50)]" data-testid="admin-auth-loading">
        <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (user === false) {
    return <AdminLogin onLogin={setUser} />;
  }

  return (
    <div className="min-h-screen bg-[var(--slate-50)]" data-testid="admin-leads-page">
      <header className="bg-navy text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="" className="h-10 w-10 rounded-xl object-cover shadow-md" />
            <div className="leading-tight">
              <p className="font-heading font-extrabold">{BRAND.name}</p>
              <p className="text-[10px] tracking-[0.18em] font-semibold text-[var(--green-500)]">PANEL DE LEADS</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white transition-colors" data-testid="admin-back-link">
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Ver landing</span>
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3.5 py-2 text-sm font-semibold transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">Leads de cotización IUL</h1>
          <p className="mt-1 text-slate-600">Personas que solicitaron su cotización gratis y recibieron el bono de $50.</p>
        </div>

        <StatsCards stats={stats} />

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o teléfono..."
              className="field !py-3 !pl-10"
              data-testid="admin-leads-search"
            />
          </div>
          <button onClick={load} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm font-semibold text-navy hover:bg-slate-50 transition-colors" data-testid="admin-refresh-button">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
          <button onClick={exportCsv} disabled={!filtered.length} className="inline-flex items-center justify-center gap-2 rounded-xl bg-navy text-white px-4 py-3 text-sm font-semibold hover:bg-[var(--navy-800)] transition-colors disabled:opacity-50" data-testid="admin-export-button">
            <Download className="h-4 w-4" /> Exportar CSV
          </button>
        </div>

        <p className="text-xs font-semibold text-slate-500" data-testid="admin-leads-count">
          Mostrando {filtered.length} de {leads.length} leads
        </p>

        <LeadsTable leads={filtered} onStatusChange={handleStatus} onDelete={handleDelete} />
      </main>
    </div>
  );
}
